import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Banknote, CheckCircle, Send, Share2, Bookmark, Calendar, Upload, Target, TrendingUp, AlertCircle, Loader2, X, FileText, Briefcase } from 'lucide-react';
import { getJobDetailBySlug, checkFitScore } from '../services/api';
import LoginModal from '../components/LoginModal';
import SEO, { jobPostingSchema, breadcrumbSchema } from '../components/SEO';
import './JobDetailPage.css';

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatSalary(start, end) {
  if (!start && !end) return null;
  if (start && end) return `${start.toLocaleString('tr-TR')} - ${end.toLocaleString('tr-TR')} TL`;
  if (start) return `${start.toLocaleString('tr-TR')} TL`;
  return `${end.toLocaleString('tr-TR')} TL`;
}

function splitLines(text) {
  if (!text) return [];
  return text.split('\n').map(l => l.trim()).filter(Boolean);
}

const JobDetailPage = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [fitCv, setFitCv] = useState(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [fitResult, setFitResult] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const fitFileRef = useRef(null);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getJobDetailBySlug(slug);
        setJob(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [slug]);

  const handleFitCheck = async () => {
    if (!fitCv || !job) return;
    setFitLoading(true);
    setFitResult(null);
    try {
      const data = await checkFitScore(fitCv, job.id);
      setFitResult(data);
    } catch {
      setFitResult({ error: true });
    } finally {
      setFitLoading(false);
    }
  };

  const removeFitCv = () => {
    setFitCv(null);
    setFitResult(null);
    if (fitFileRef.current) fitFileRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="job-detail__not-found">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-detail-page">
        <div className="job-detail__not-found">
          <h2>İlan Bulunamadı</h2>
          <Link to="/" className="btn btn--primary">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  const salary = formatSalary(job.startSalary, job.endSalary);
  const descriptionLines = splitLines(job.description);
  const requirementLines = splitLines(job.requirements);
  const responsibilityLines = splitLines(job.responsibilities);
  const qualificationLines = splitLines(job.preferredQualifications);
  const publishDate = formatDate(job.startAt);
  const endDate = formatDate(job.endAt);

  const handleApply = () => {
    if (job.applyUrl) {
      window.open(job.applyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: job.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="job-detail-page">
      {job && (
        <SEO
          title={`${job.title} - ${job.companyName || ''}`}
          description={`${job.title} iş ilanı${job.companyName ? ` - ${job.companyName}` : ''}${job.city ? ` - ${job.city}` : ''}. isBulsun.com'da detayları gör ve hemen başvur.`}
          path={`/ilan/${slug}`}
          type="article"
          structuredData={[jobPostingSchema(job), breadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: job.title }])]}
        />
      )}
      {/* Hero */}
      <div className="job-detail__hero">
        <div className="job-detail__hero-container">
          <Link to="/" className="job-detail__back">
            <ArrowLeft size={18} />
            <span>Geri Dön</span>
          </Link>

          <h1 className="job-detail__title">{job.title}</h1>

          <div className="job-detail__meta">
            {job.city && (
              <div className="job-detail__meta-item">
                <MapPin size={15} />
                <span>{job.city}</span>
              </div>
            )}
            {job.workingType && (
              <div className="job-detail__meta-item">
                <Briefcase size={15} />
                <span>{job.workingType}</span>
              </div>
            )}
            {salary && (
              <div className="job-detail__meta-item">
                <Banknote size={15} />
                <span>{salary}</span>
              </div>
            )}
            {publishDate && (
              <div className="job-detail__meta-item">
                <Calendar size={15} />
                <span>Yayınlanma: {publishDate}</span>
              </div>
            )}
            {endDate && (
              <div className="job-detail__meta-item job-detail__meta-item--end">
                <AlertCircle size={15} />
                <span>Son Başvuru: {endDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="job-detail__container">
        <div className="job-detail__layout">
          {/* Main */}
          <div className="job-detail__main">
            {job.matchScore != null && job.matchScore > 0 && (
              <div className="job-detail__match-section">
                <div className="job-detail__match-header">
                  <span>AI Uyum Puanı</span>
                  <span className="job-detail__match-value">%{job.matchScore}</span>
                </div>
                <div className="job-detail__match-bar">
                  <div className="job-detail__match-fill" style={{ width: `${job.matchScore}%` }}></div>
                </div>
              </div>
            )}

            {descriptionLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <Briefcase size={18} />
                  İş Tanımı
                </h2>
                <ul className="job-detail__list">
                  {descriptionLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {requirementLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <Target size={18} />
                  Aranan Nitelikler
                </h2>
                <ul className="job-detail__list">
                  {requirementLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {responsibilityLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <CheckCircle size={18} />
                  Sorumluluklar
                </h2>
                <ul className="job-detail__list">
                  {responsibilityLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {qualificationLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <TrendingUp size={18} />
                  Tercih Edilen Nitelikler
                </h2>
                <ul className="job-detail__list">
                  {qualificationLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="job-detail__sidebar">
            <div className="job-detail__apply-card">
              <h3 className="job-detail__apply-title">Bu Pozisyona Başvur</h3>
              <p className="job-detail__apply-desc">Hemen başvurunu yap!</p>
              <button className="btn btn--primary btn--full" onClick={handleApply}>
                <Send size={16} />
                Başvur
              </button>
              <div className="job-detail__apply-actions">
                <button className="job-detail__action-btn">
                  <Bookmark size={16} />
                  Kaydet
                </button>
                <button className="job-detail__action-btn" onClick={handleShare}>
                  <Share2 size={16} />
                  Paylaş
                </button>
              </div>
            </div>

            <div className="job-detail__fit-card">
              <div className="job-detail__fit-header">
                <Target size={20} />
                <h3 className="job-detail__fit-title">Uygunluğunu Ölç</h3>
              </div>
              <p className="job-detail__fit-desc">
                CV'ni yükle, bu pozisyon için uygunluk puanını ve kariyer önerilerini gör.
              </p>

              {!fitResult && (
                <>
                  <input
                    ref={fitFileRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="job-detail__fit-file-input"
                    id="fit-cv-upload"
                    onChange={(e) => { if (e.target.files[0]) setFitCv(e.target.files[0]); }}
                  />
                  {fitCv ? (
                    <div className="job-detail__fit-cv-badge">
                      <FileText size={14} />
                      <span>{fitCv.name}</span>
                      <button type="button" onClick={removeFitCv} className="job-detail__fit-cv-remove">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="fit-cv-upload" className="job-detail__fit-upload-btn">
                      <Upload size={16} />
                      CV Yükle
                    </label>
                  )}
                  <button className="btn btn--fit btn--full" onClick={handleFitCheck} disabled={!fitCv || fitLoading}>
                    {fitLoading ? (
                      <><Loader2 size={16} className="job-detail__spinner" /> Hesaplanıyor...</>
                    ) : (
                      <><Target size={16} /> Puanımı Hesapla</>
                    )}
                  </button>
                </>
              )}

              {fitResult && !fitResult.error && (
                <div className="job-detail__fit-result">
                  <div className="job-detail__fit-score">
                    <div className="job-detail__fit-score-circle">
                      <span className="job-detail__fit-score-value">{fitResult.score ?? fitResult.matchScore ?? 0}</span>
                      <span className="job-detail__fit-score-label">puan</span>
                    </div>
                  </div>
                  {(fitResult.summary || fitResult.message) && (
                    <p className="job-detail__fit-summary">{fitResult.summary || fitResult.message}</p>
                  )}
                  {fitResult.strengths && fitResult.strengths.length > 0 && (
                    <div className="job-detail__fit-section">
                      <h4 className="job-detail__fit-section-title job-detail__fit-section-title--green">
                        <CheckCircle size={14} /> Güçlü Yönlerin
                      </h4>
                      <ul className="job-detail__fit-list">
                        {fitResult.strengths.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {fitResult.weaknesses && fitResult.weaknesses.length > 0 && (
                    <div className="job-detail__fit-section">
                      <h4 className="job-detail__fit-section-title job-detail__fit-section-title--orange">
                        <AlertCircle size={14} /> Geliştirmen Gerekenler
                      </h4>
                      <ul className="job-detail__fit-list">
                        {fitResult.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  {fitResult.suggestions && fitResult.suggestions.length > 0 && (
                    <div className="job-detail__fit-section">
                      <h4 className="job-detail__fit-section-title job-detail__fit-section-title--blue">
                        <TrendingUp size={14} /> Kariyer Önerileri
                      </h4>
                      <ul className="job-detail__fit-list">
                        {fitResult.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  <button className="job-detail__fit-retry" onClick={removeFitCv}>Farklı CV ile Tekrar Dene</button>
                </div>
              )}

              {fitResult && fitResult.error && (
                <div className="job-detail__fit-error">
                  <AlertCircle size={16} />
                  <span>Skor hesaplanamadı. Lütfen tekrar deneyin.</span>
                  <button className="job-detail__fit-retry" onClick={removeFitCv}>Tekrar Dene</button>
                </div>
              )}
            </div>

            <div className="job-detail__info-card">
              <h4 className="job-detail__info-title">İlan Bilgileri</h4>
              {job.city && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Konum</span>
                  <span className="job-detail__info-value">{job.city}</span>
                </div>
              )}
              {job.workingType && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Çalışma Şekli</span>
                  <span className="job-detail__info-value">{job.workingType}</span>
                </div>
              )}
              {salary && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Maaş</span>
                  <span className="job-detail__info-value">{salary}</span>
                </div>
              )}
              {publishDate && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Yayınlanma</span>
                  <span className="job-detail__info-value">{publishDate}</span>
                </div>
              )}
              {endDate && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Son Başvuru</span>
                  <span className="job-detail__info-value">{endDate}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
};

export default JobDetailPage;
